import React, { useEffect, useState } from 'react';
import CommonModal from '../../Components/CommonModal';
import CommonTable from '../../Components/CommonTable';
import ButtonLoader from '../../Components/ButtonLoader';
import StatusBadge from '../../Components/StatusBadge';
import { vendors_with_incidents, vendors_with_critical_business_impact } from '../../services';
import { MISSING_TEXT, severityTone } from '../tpcrmUtils';
import '../../styles/TpcrmPortal.scss';

// Real issue-level drill-down behind the Analytics KPI cards — the cards
// themselves only ever show a count (sourced from vendor-trend's metadata,
// see TpcrmAnalyticsView.js), and previously clicking through just filtered
// the Vendors table by grade/criticality as an approximation. That filter
// never matched the actual incident/critical-impact records (a vendor can be
// grade F without an incident this period, or vice versa) and showed no
// issue detail (category/severity/count) at all. These two dedicated
// endpoints already return the real per-vendor issue lists — this modal is
// the Portal's missing equivalent of the old dashboard's
// VendorsWithIncidents.js / VendorsWithCriticalImpact.js.
const VARIANTS = {
    incidents: {
        title: 'Vendors with Incidents',
        service: vendors_with_incidents,
        size: 'xl',
        headers: ['Vendor Name', 'Category', 'Issue', 'Severity', 'Count'],
        emptyTitle: 'No incidents in this period',
        emptyHint: 'No vendor issues were recorded for the selected date range.',
        mapRow: (vendor, issue, key) => [
            vendor?.vendor_name || <span className="tpcrm-cell-empty">{MISSING_TEXT.vendor}</span>,
            issue?.category || <span className="tpcrm-cell-empty">{MISSING_TEXT.category}</span>,
            issue?.description || <span className="tpcrm-cell-empty">{MISSING_TEXT.issue}</span>,
            issue?.severity
                ? <StatusBadge key={`sev-${key}`} dot={false} style={severityTone(issue.severity)}>{issue.severity}</StatusBadge>
                : <span key={`sev-${key}`} className="tpcrm-cell-empty">{MISSING_TEXT.severity}</span>,
            issue?.count ?? <span className="tpcrm-cell-empty">{MISSING_TEXT.count}</span>,
        ],
    },
    critical: {
        title: 'Vendors with Critical Business Impact',
        service: vendors_with_critical_business_impact,
        size: 'lg',
        headers: ['Vendor Name', 'Issue'],
        emptyTitle: 'No critical impact vendors in this period',
        emptyHint: 'No vendors with critical business impact were found for the selected date range.',
        mapRow: (vendor, issue) => [
            vendor?.vendor_name || <span className="tpcrm-cell-empty">{MISSING_TEXT.vendor}</span>,
            issue?.description || <span className="tpcrm-cell-empty">{MISSING_TEXT.issue}</span>,
        ],
    },
};

function VendorIssuesModal({ show, onHide, variant }) {
    const config = variant ? VARIANTS[variant] : null;
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);

    // No date filter here on purpose: the KPI cards that open this modal are
    // a fixed "last 30 days" stat, and these two endpoints only accept a
    // single exact-day `analysis_date` (no start/end window) — pinning to
    // one specific day almost never matches the 30-day count that made the
    // user click through in the first place. Querying with no date param
    // matches what the old dashboard's own VendorsWithIncidents.js/
    // VendorsWithCriticalImpact.js already did by default.
    useEffect(() => {
        if (!show || !config) return undefined;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const response = await config.service({});
                const data = response?.statusCode?.response_data?.data || [];
                if (!cancelled) setVendors(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error(`Failed to load ${config.title}:`, error);
                if (!cancelled) setVendors([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [show, config]);

    if (!config) return null;

    const rows = vendors.flatMap((vendor, vIdx) =>
        (vendor?.issues || []).map((issue, iIdx) => ({
            data: config.mapRow(vendor, issue, `${vIdx}-${iIdx}`),
        })),
    );

    return (
        <CommonModal show={show} onHide={onHide} title={config.title} size={config.size}>
            {loading ? (
                <div className="tpcrm-loader"><ButtonLoader /></div>
            ) : rows.length ? (
                <div className="tpcrm-profile-table-wrap">
                    <CommonTable headers={config.headers} rows={rows} noPadding noScroll />
                </div>
            ) : (
                <div className="tpcrm-profile-empty">
                    <div className="tpcrm-profile-empty-icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 3H5a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9l-6-6H9z" />
                            <path d="M13 3v6h6" />
                            <path d="M9 13h6M9 17h4" />
                        </svg>
                    </div>
                    <div className="tpcrm-profile-empty-title">{config.emptyTitle}</div>
                    <p className="tpcrm-profile-empty-hint">{config.emptyHint}</p>
                </div>
            )}
        </CommonModal>
    );
}

export default VendorIssuesModal;
